import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import 'rxjs/add/operator/takeUntil';
import { AssetPage, EmployeeAsset, WorkflowError } from '../../../models/employee-asset.model';
import { EmployeeAssetsService } from '../../../services/employee-assets.service';
import { AssetIssueModalComponent } from './asset-issue-modal.component';
import { AssetActionModalComponent } from './asset-action-modal.component';
import { ASSET } from './asset.constants';
import { assetErrorKey, safeServiceUrl, workflowTrackBy } from './asset.helpers';

@Component({selector: 'app-asset-profile-issue', templateUrl: './asset-profile-issue.component.html',
  styleUrls: ['./employee-assets.component.css']})
export class AssetProfileIssueComponent implements OnChanges, OnDestroy {
  @Input() employeeId: string;
  @Input() active = false;
  @Input() compactActions = false;
  @Output() changed = new EventEmitter<void>();
  @Output() viewReady = new EventEmitter<void>();
  items: EmployeeAsset[] = [];
  trackBy = workflowTrackBy;
  safeUrl = safeServiceUrl;
  constants = ASSET;
  canIssue = false;
  canIssueIt = false;
  canIssueService = false;
  hasMore = false;
  page = ASSET.zero;
  loading = false;
  readable = false;
  busy = false;
  errorKey = ASSET.empty;
  private request: Subscription;
  private dialog: NgbModalRef;
  private disposed = false;
  private generation = ASSET.zero;
  private destroyed = new Subject<void>();
  constructor(private api: EmployeeAssetsService, private modals: NgbModal, private changeDetectorRef: ChangeDetectorRef) {}
  get mayIssue(): boolean { return this.active && (this.canIssue || this.canIssueIt || this.canIssueService); }
  ngOnChanges(): void {
    this.generation++; this.closeDialog(); this.cancelRead(); this.clear();
    if (this.employeeId) { this.load(); }
    this.changeDetectorRef.markForCheck();
  }
  ngOnDestroy(): void {
    this.disposed = true; this.generation++; this.closeDialog(); this.cancelRead(); this.destroyed.next(); this.destroyed.complete();
  }
  load(more = false): void {
    if (this.disposed || !this.employeeId || this.busy || more && this.loading) { return; }
    this.cancelRead(); if (!more) { this.clear(); }
    this.loading = true; this.errorKey = ASSET.empty;
    const read = {employeeId: this.employeeId, generation: ++this.generation,
      page: more ? this.page + ASSET.one : ASSET.zero, more: more};
    this.request = this.api.list({employeeId: read.employeeId, view: ASSET.all, page: read.page}).takeUntil(this.destroyed)
      .subscribe(result => this.applied(read, result), error => this.failed(read.employeeId, read.generation, error));
    this.changeDetectorRef.markForCheck();
  }
  open(): void {
    if (this.disposed || !this.mayIssue || this.loading || this.dialog) { return; }
    const ref = this.modals.open(AssetIssueModalComponent, {size: ASSET.modalSize, beforeDismiss: () => !ref.componentInstance.busy});
    ref.componentInstance.employeeId = this.employeeId;
    ref.componentInstance.canIssue = this.canIssue; ref.componentInstance.canIssueIt = this.canIssueIt;
    ref.componentInstance.canIssueService = this.canIssueService;
    this.watch(ref);
  }
  canClose(item: EmployeeAsset): boolean {
    return (item.allowedActions || []).indexOf(ASSET.revokeAccess) >= ASSET.zero;
  }
  closeAccess(item: EmployeeAsset): void {
    if (this.disposed || this.loading || this.dialog || this.items.indexOf(item) < ASSET.zero || !this.canClose(item)) { return; }
    const ref = this.modals.open(AssetActionModalComponent, {size: ASSET.modalSize, beforeDismiss: () => !ref.componentInstance.busy});
    ref.componentInstance.asset = item; ref.componentInstance.action = ASSET.revokeAccess; this.watch(ref);
  }
  private watch(ref: NgbModalRef): void {
    this.dialog = ref; this.busy = true;
    ref.result.then(result => this.closed(ref, !!result), () => this.closed(ref, false));
  }
  private applied(read: ProfileAssetRead, result: AssetPage): void {
    if (!this.current(read.employeeId, read.generation)) { return; }
    this.canIssue = !!result && result.canIssue === true; this.canIssueIt = !!result && result.canIssueIt === true;
    this.canIssueService = !!result && result.canIssueService === true;
    const items = (result && result.items) || [];
    this.items = read.more ? this.items.concat(items) : items;
    this.page = read.page; this.hasMore = !!result && result.hasMore === true;
    this.loading = false; this.readable = true; this.changeDetectorRef.markForCheck();
    this.viewReady.emit();
  }
  private failed(employee: string, generation: number, error: WorkflowError): void {
    if (!this.current(employee, generation)) { return; }
    this.loading = false;
    if (error && (error.status === ASSET.forbidden || error.status === ASSET.unauthorized)) { this.clear(); }
    else { this.errorKey = assetErrorKey(error, false); }
    this.changeDetectorRef.markForCheck();
    this.viewReady.emit();
  }
  private current(employee: string, generation: number): boolean { return !this.disposed && this.employeeId === employee && this.generation === generation; }
  private closed(ref: NgbModalRef, saved: boolean): void {
    if (this.dialog !== ref) { return; }
    this.dialog = null; this.busy = false;
    if (this.disposed) { return; }
    if (saved) { this.load(); this.changed.emit(); } else { this.changeDetectorRef.markForCheck(); }
  }
  private closeDialog(): void {
    if (this.dialog) { const ref = this.dialog; this.dialog = null; this.busy = false; ref.close(); }
  }
  private cancelRead(): void { if (this.request) { this.request.unsubscribe(); } this.loading = false; }
  private clear(): void {
    this.canIssue = false; this.canIssueIt = false; this.canIssueService = false;
    this.items = []; this.page = ASSET.zero; this.hasMore = false; this.errorKey = ASSET.empty; this.readable = false;
  }
}
interface ProfileAssetRead {employeeId: string; generation: number; page: number; more: boolean;}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/employee-assets/asset-profile-issue.component.ts